import { createAsyncThunk } from '@/redux/createAsyncThunk';
import { getAuthToken, getZipCodeGeoCoordinates } from '@/redux/modules/account/user/user.selectors';
import { getIsBot } from '@/redux/modules/analytics';
import { getDeployment } from '@/redux/modules/config';
import { getUserExcludedFacets } from '@/redux/modules/search/exclusions/searchExclusions.selectors';
import { clearPromotedSearchResults, searchPromoted } from '@/redux/modules/searchPromoted';
import { getSellerName } from '@/redux/modules/seller';
import { GlobalState } from '@/redux/store';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { SearchPageName } from '@/types/search/searchQuery';
import { isClientSide } from '@/utils/amplitude/analytics/core';
import {
    PriceResultsMatch,
    QueryIntent,
    QueryIntentFromApi,
    SearchContext,
} from '@/utils/amplitude/analytics/core.types';
import {
    trackSearchCompletedAnalytics,
    TrackSearchCompletedSearchType,
} from '@/utils/amplitude/analytics/search/trackSearchCompleted.analytics';
import { trackSearchRefinedAnalytics } from '@/utils/amplitude/analytics/search/trackSearchRefined.analytics';
import { createAmplitudeSearchQueryParams } from '@/utils/amplitude/analytics/utils/createAmplitudeSearchQueryParams';
import { getFilteredFieldFromFilterAction } from '@/utils/amplitude/analytics/utils/getFilteredFieldFromFilterAction';
import { getFullUrl } from '@/utils/amplitude/analytics/utils/getFullUrl';
import { resolveSemanticBrowseDimensionValuesInSearchQuery } from '@/utils/browsePath/browsePath';
import { AmplitudePageName } from '@/utils/getPageNameFromPath';
import { parseQueryParams } from '@/utils/queryParams';

import { getClientIpAddress } from '../browser';
import { getSearchPageSize } from '../pagination';
import { getSearchFacets } from '../searchFacetAggregates.selectors';
import { fetchCategoryResults, fetchSearchResults } from './search.api';
import { getAugmentedSearchQuery, getSearchCompletedTrigger } from './search.helpers';
import {
    getAggregationsOnDemandStatus,
    getCurrentSearch,
    getLastSearchFetchOptions,
    getSearchedItemIds,
    getSearchSellerItemsSearch,
    getSearchSellerItemsSearchSellerId,
    getSearchSubmittedFromPage,
    getSearchTotalFound,
    getSearchTotalSoldItems,
    getSearchUiState,
    getUseGeoLocation,
} from './search.selectors';
import {
    FetchCategoryResultsParams,
    FetchSearchResultsParams,
    isErrorResponse,
    LOAD_SEARCH_RESULTS_SUCCESS,
    LOAD_SEARCH_RESULTS_SUCCESS_ACTION,
    QueryModifications,
    SAVE_LAST_SEARCH_FETCH_OPTIONS,
    SearchResultPayload,
    SubmitSearchPayload,
} from './search.types';

// Discriminator for `lastSearchFetchOptions`. The category variant carries
// `isCategorySearch: true` so aggs-on-demand can replay the right endpoint.
type CategorySearchLastOptions = FetchCategoryResultsParams & { isCategorySearch: true };

const isCategorySearchOptions = (
    options: FetchSearchResultsParams | CategorySearchLastOptions
): options is CategorySearchLastOptions => 'isCategorySearch' in options && options.isCategorySearch === true;

export const submitSearch = createAsyncThunk<SearchResultPayload | string, SubmitSearchPayload>(
    'la/domain/search/submitSearch',
    async (
        {
            learningToRankModelVersion,
            pageName,
            popularityScoreReRankingVersion,
            rankingVersionVariant,
            routerState = {},
            searchQuery,
            skipAggs,
            useScoutCategories,
        },
        { dispatch, getState }
    ) => {
        const state = getState();
        const deployment = getDeployment(state);
        const authToken = getAuthToken(state);
        const ipAddress = getClientIpAddress(state);
        const isBot = getIsBot(state);
        const currentSearch = getCurrentSearch(state);
        const submittedFromPage = getSearchSubmittedFromPage(state);
        const { isZeroResults } = getSearchUiState(state);

        // Annotate facets which have user-defined search exclusions
        const searchExclusions = getUserExcludedFacets(state);
        const searchQueryForRequest = resolveSemanticBrowseDimensionValuesInSearchQuery(
            searchQuery,
            getSearchFacets(state)
        );
        const augmentedSearchQuery = getAugmentedSearchQuery(searchQueryForRequest, searchExclusions);

        // Set seoSearch to true if the page is an SEO search or a collections page
        const searchQueryWithSeo = {
            ...augmentedSearchQuery,
            seoSearch: pageName === SearchPageName.SEO_SEARCH || pageName === SearchPageName.COLLECTIONS,
        };

        const searchForCacheCheck = JSON.stringify(searchQueryWithSeo);
        if (searchForCacheCheck === currentSearch && !isZeroResults) {
            const cachedSearchUiState = getSearchUiState(state);
            trackSearchCompletedOrRefined({
                deployment,
                itemScores: cachedSearchUiState.itemScores,
                lotIds: getSearchedItemIds(state),
                queryModifications: cachedSearchUiState.queryModifications,
                routerState,
                searchTerm: searchQuery.searchTerm,
                state,
                submittedFromPage,
                totalFound: getSearchTotalFound(state),
                totalSoldItems: getSearchTotalSoldItems(state),
            });
            return {};
        }

        const useGeoLocation = getUseGeoLocation(state);
        const geoCoordinates = getZipCodeGeoCoordinates(state);

        // searchQueryWithSeo contains search query + seoSearch flag to request SEO metadata from API if on SEO search page or collections page
        const options = {
            authToken,
            deployment,
            geoCoordinates,
            ipAddress,
            isBot,
            learningToRankModelVersion,
            popularityScoreReRankingVersion,
            rankingVersionVariant,
            searchQuery: searchQueryWithSeo,
            skipAggs,
            useGeoLocation,
        };

        // Save options so we can re-run without skipAggs later for on-demand facets
        dispatch({
            payload: options,
            type: SAVE_LAST_SEARCH_FETCH_OPTIONS,
        });

        // main search api dispatch
        const promises: Promise<any>[] = [fetchSearchResults(options)];
        // add search for promoted items if making a catalog search AND sort is Most Relevant
        if (pageName !== 'catalog' && searchQueryForRequest.sort === SearchSortAndDirection.MostRelevant) {
            promises.push(
                dispatch(
                    searchPromoted({
                        pageName,
                        rankingVersionVariant,
                        searchQuery: searchQueryForRequest,
                        useScoutCategories,
                    })
                )
            );
        } else if (pageName !== 'catalog' && searchQueryForRequest.sort !== SearchSortAndDirection.MostRelevant) {
            // Clear promoted search results when sort is not Most Relevant
            dispatch(clearPromotedSearchResults());
        }

        const [results] = await Promise.all(promises);

        if (isErrorResponse(results)) {
            throw new Error(results.payload);
        }

        results.payload.currentQueryKey = searchForCacheCheck;

        // TODO: once all the reducers that subscribe to LOAD_SEARCH_RESULTS_SUCCESS are updated to toolkit, this dispatch should be removed
        const successAction: LOAD_SEARCH_RESULTS_SUCCESS_ACTION = {
            error: false,
            meta: { actionTime: Date.now() },
            payload: skipAggs ? { ...results.payload, soldItemFacets: null } : results.payload,
            type: LOAD_SEARCH_RESULTS_SUCCESS,
        };

        dispatch(successAction);

        if (!isZeroResults) {
            trackSearchCompletedOrRefined({
                deployment,
                itemScores: results.payload.itemScores,
                lotIds: results.payload.itemIDs,
                queryModifications: results.payload.queryModifications,
                routerState,
                searchTerm: searchQuery.searchTerm,
                state,
                submittedFromPage,
                totalFound: results.payload.totalFound,
                totalSoldItems: results.payload.totalSold,
            });
        }

        // Null out soldItemFacets on skipAggs so addSearchResults doesn't clobber soldItemFacets
        // populated in parallel by fetchSearchAggregationsOnDemand.fulfilled. `facets` is preserved
        // so search-party's synthesized selected-chip facets (skipAggs responses) reach the UI.
        return skipAggs ? { ...results.payload, soldItemFacets: null } : results.payload;
    }
);

type TrackSearchCompletedParams = {
    deployment: ReturnType<typeof getDeployment>;
    itemScores?: Record<string, number>;
    lotIds: number[];
    queryModifications?: QueryModifications;
    routerState: SubmitSearchPayload['routerState'];
    searchTerm: string;
    state: GlobalState;
    submittedFromPage: AmplitudePageName;
    totalFound: number;
    totalSoldItems: number;
};

const trackSearchCompletedOrRefined = ({
    deployment,
    itemScores,
    lotIds,
    queryModifications,
    routerState,
    searchTerm,
    state,
    submittedFromPage,
    totalFound,
    totalSoldItems,
}: TrackSearchCompletedParams) => {
    if (isClientSide()) {
        const pageSize = getSearchPageSize(state);
        const sanitizedAnalyticsQueryParams = createAmplitudeSearchQueryParams(
            window?.location?.search ?? '',
            pageSize
        );

        const analyticsLotIds = lotIds.join(',');
        const serializedItemScores =
            itemScores && Object.keys(itemScores).length > 0 ? JSON.stringify(itemScores) : undefined;

        // Facet applied (and not a related-search suggestion click). Tracking Search Refined
        if (routerState.filterAction && !routerState.suggestionClicked) {
            const filteredField = getFilteredFieldFromFilterAction(routerState.filterAction);
            const filteredFields = filteredField ? [filteredField] : [];
            const trackSearchRefinedPayload = {
                filteredFields,
                itemScores: serializedItemScores,
                lotIds: analyticsLotIds,
                normalizedSearchQuery: searchTerm?.toLowerCase() ?? '',
                numberOfSearchResults: totalFound,
                pageName: submittedFromPage,
                searchQuery: searchTerm,
                url: getFullUrl(deployment, window.location.pathname),
                ...sanitizedAnalyticsQueryParams,
            };
            trackSearchRefinedAnalytics(trackSearchRefinedPayload);
        } else {
            // No facet applied. Tracking Search Completed
            const isSellerItemsSearch = getSearchSellerItemsSearch(state);
            const sellerId = getSearchSellerItemsSearchSellerId(state);
            const sellerName = getSellerName(state, sellerId);

            const fromSellerPage = isSellerItemsSearch && !submittedFromPage && !searchTerm;

            const searchInput = document.getElementById('search-input') as HTMLInputElement;
            const submittedFromSearchBar = searchInput?.value === searchTerm;

            // This gets added in useSearchProps.ts
            const searchSuggestionUsed = Boolean(routerState?.suggestionClicked);

            // Determine search context based on URL parameters
            const queryParams = parseQueryParams(window?.location?.search ?? '');
            const { pathname } = window.location;
            const isArchivedSearch = queryParams.status === 'archive' || pathname.includes('/price-guide');
            const searchContext = isArchivedSearch ? SearchContext.PriceResults : SearchContext.UpcomingItems;

            let queryIntent: QueryIntent | undefined = QueryIntent.None;
            if (queryModifications?.queryRewrite?.applied) {
                const queryIntentApiValue = queryModifications?.queryRewrite?.queryIntent;
                queryIntent = queryIntentApiValue
                    ? (QueryIntentFromApi[queryIntentApiValue] ?? QueryIntent.None)
                    : QueryIntent.None;
            }

            let modifiedQuery: string | undefined;
            if (queryIntent !== QueryIntent.None) {
                const modifiedQueryValue = queryModifications?.queryRewrite?.modifiedQuery;
                modifiedQuery = modifiedQueryValue === '' ? '(empty)' : modifiedQueryValue;
            }

            const trackSearchCompletedPayload = {
                itemScores: serializedItemScores,
                lotIds: analyticsLotIds,
                modifiedQuery,
                normalizedSearchQuery: (fromSellerPage ? sellerName : searchTerm)?.toLowerCase() ?? '',
                numberOfSearchResults: totalFound,
                page: sanitizedAnalyticsQueryParams.page,
                pageName: fromSellerPage ? AmplitudePageName.Auctioneer : submittedFromPage,
                pageSize,
                priceResultsMatch: totalSoldItems > 0 ? PriceResultsMatch.Yes : PriceResultsMatch.No,
                queryIntent,
                searchContext,
                searchQuery: fromSellerPage ? sellerName : searchTerm,
                searchSuggestionUsed,
                searchType: fromSellerPage
                    ? TrackSearchCompletedSearchType.Auctioneer
                    : TrackSearchCompletedSearchType.Keyword,
                trigger: getSearchCompletedTrigger(
                    sanitizedAnalyticsQueryParams.trigger,
                    searchSuggestionUsed,
                    submittedFromSearchBar
                ),
                url: getFullUrl(deployment, window.location.pathname),
            };

            trackSearchCompletedAnalytics(trackSearchCompletedPayload);
        }
    }
};

export const updateSearchSubmittedFromPage = createAsyncThunk<AmplitudePageName, AmplitudePageName>(
    'la/domain/search/updateSearchSubmittedFromPage',
    (pageName) => pageName
);

export const updateSearchSellerItemsSearch = createAsyncThunk<boolean, boolean>(
    'la/domain/search/updateSearchSellerItemsSearch',
    (sellerItemsSearch) => sellerItemsSearch
);

export const updateSearchSellerItemsSearchSellerId = createAsyncThunk<number, number>(
    'la/domain/search/updateSearchSellerItemsSearchSellerId',
    (sellerItemsSearch) => sellerItemsSearch
);

export const fetchMoreSearchResults = createAsyncThunk<SearchResultPayload | string, SubmitSearchPayload>(
    'la/domain/search/fetchMoreSearchResults',
    async ({ pageName, searchQuery, skipAggs, useScoutCategories }, { dispatch, getState }) => {
        const state = getState();
        const deployment = getDeployment(state);
        const authToken = getAuthToken(state);
        const ipAddress = getClientIpAddress(state);
        const isBot = getIsBot(state);

        // Annotate facets which have user-defined search exclusions
        const searchExclusions = getUserExcludedFacets(state);
        const searchQueryForRequest = resolveSemanticBrowseDimensionValuesInSearchQuery(
            searchQuery,
            getSearchFacets(state)
        );
        const augmentedSearchQuery = getAugmentedSearchQuery(
            { ...(isBot ? { isBot } : {}), ...searchQueryForRequest },
            searchExclusions
        );

        const useGeoLocation = getUseGeoLocation(state);
        const geoCoordinates = getZipCodeGeoCoordinates(state);

        const promises: Promise<any>[] = [
            fetchSearchResults({
                authToken,
                deployment,
                geoCoordinates,
                ipAddress,
                searchQuery: augmentedSearchQuery,
                skipAggs,
                useGeoLocation,
            }),
        ];

        if (pageName !== 'catalog' && searchQueryForRequest.sort === SearchSortAndDirection.MostRelevant) {
            promises.push(
                dispatch(searchPromoted({ pageName, searchQuery: searchQueryForRequest, useScoutCategories }))
            );
        } else if (pageName !== 'catalog' && searchQueryForRequest.sort !== SearchSortAndDirection.MostRelevant) {
            // Clear promoted search results when sort is not Most Relevant
            dispatch(clearPromotedSearchResults());
        }

        const [results] = await Promise.all(promises);

        if (isErrorResponse(results)) {
            throw new Error(results.payload);
        }

        // Defensive: no caller passes skipAggs=true to pagination today, but mirror the
        // null-out so a future opt-in can't reopen the addSearchResults race. `facets` is
        // preserved so synthesized selected-chip facets from search-party reach the UI.
        return skipAggs ? { ...results.payload, soldItemFacets: null } : results.payload;
    }
);

/**
 * Re-runs the last search WITHOUT skipAggs to fetch search-scoped facets on demand.
 * Only updates facets in state — does NOT update items, loading, or other search state.
 * Skips the fetch if facets are already loaded or currently loading.
 */
export const fetchSearchAggregationsOnDemand = createAsyncThunk<SearchResultPayload | null, void>(
    'la/domain/search/fetchSearchAggregationsOnDemand',
    async (_, { dispatch, getState }) => {
        const state = getState();
        const lastOptions = state.search.lastSearchFetchOptions;
        if (!lastOptions) {
            return null;
        }

        const results = isCategorySearchOptions(lastOptions)
            ? await fetchCategoryResults({
                  ...lastOptions,
                  aggsOnly: true,
                  skipAggs: undefined,
              })
            : await fetchSearchResults({
                  ...lastOptions,
                  aggsOnly: true,
                  skipAggs: undefined,
              });

        if (isErrorResponse(results)) {
            throw new Error('Failed to fetch search aggregations');
        }

        // Dispatch legacy action so other reducers pick up search results
        const successAction: LOAD_SEARCH_RESULTS_SUCCESS_ACTION = {
            error: false,
            meta: { actionTime: Date.now() },
            payload: results.payload,
            type: LOAD_SEARCH_RESULTS_SUCCESS,
        };
        dispatch(successAction);

        return results.payload;
    },
    {
        condition: (_, { getState }) => {
            const state = getState();
            const aggregationsOnDemandStatus = getAggregationsOnDemandStatus(state);
            const lastSearchFetchOptions = getLastSearchFetchOptions(state);

            // Proceed only when the last search skipped aggs and we haven't already
            // loaded (or started loading) the on-demand aggregation result.
            const aggregationsAlreadyInFlightOrLoaded =
                aggregationsOnDemandStatus === 'loading' || aggregationsOnDemandStatus === 'loaded';
            const lastSearchSkippedAggs = Boolean(lastSearchFetchOptions?.skipAggs);

            return !aggregationsAlreadyInFlightOrLoaded && lastSearchSkippedAggs;
        },
    }
);

export const setUseGeoLocation = createAsyncThunk<boolean, boolean>(
    'la/domain/search/setUseGeoLocation',
    async (useGeoLocation) => useGeoLocation
);

export const setIsZeroResults = createAsyncThunk<boolean, boolean>(
    'la/domain/search/setIsZeroResults',
    async (isZeroResults) => isZeroResults
);
